import EventEmitter from 'events';
import uuid from 'uuid/v4';
import { isFunction } from 'lodash';

const JSON_RPC_VERSION = '2.0';
const JSON_RPC_PROP_NAME = 'jsonrpc';

const CrossWindowRPC = (channel, methodHandler = () => {}) => {
  let isDestroyed = false;

  const events = new EventEmitter();

  const {
    window: otherWindow,
    origin: expectedOrigin
  } = channel;

  if (!isFunction(methodHandler)) {
    const handlers = methodHandler;
    methodHandler = (method, ...params) => handlers[method](...params);
  }

  const onMessage = (event) => {
    const { data, origin } = event;
    const matchesOrigin = expectedOrigin === '*' || origin === expectedOrigin;

    if (!matchesOrigin) {
      return;
    }

    if (data[JSON_RPC_PROP_NAME] != JSON_RPC_VERSION) { // eslint-disable-line eqeqeq
      return;
    }

    if (data.method) {
      events.emit('request', data);
    } else {
      events.emit('response', data);
    }
  };

  window.addEventListener('message', onMessage);

  const sendMessage = (message) => otherWindow.postMessage(message, expectedOrigin);

  const sendMethod = (method, ...params) => new Promise((resolve, reject) => {
    const rejectAsDestroyed = () => reject(new Error('RPC Channel destroyed'));
    if (isDestroyed) {
      rejectAsDestroyed();
      return;
    }
    const request = {
      method,
      params,
      id: uuid(),
      [JSON_RPC_PROP_NAME]: JSON_RPC_VERSION
    };

    const cleanup = () => {
      events.removeListener('response', handleResponse);
      events.removeListener('destroy', onDestroy);
    };

    const onDestroy = () => {
      cleanup();
      rejectAsDestroyed();
    };

    const handleResponse = (response) => {
      const { id, result, error } = response;

      if (id !== request.id) {
        return;
      }

      cleanup();

      if (error) {
        reject(error);
      } else {
        resolve(result);
      }
    };

    events.addListener('response', handleResponse);
    events.addListener('destroy', onDestroy);
    sendMessage(request);
  });

  events.addListener('request', (data) => {
    const { id, method, params } = data;

    const respond = ({ result, error }) => sendMessage({
      id,
      result,
      error,
      [JSON_RPC_PROP_NAME]: JSON_RPC_VERSION
    });

    new Promise((resolve) => {
      resolve(methodHandler(method, ...params));
    })
      .then((result) => respond({ result }))
      .catch((error) => respond({ error }));
  });

  const destroy = () => {
    isDestroyed = true;
    events.emit('destroy');
    events.removeAllListeners();
    window.removeEventListener('message', onMessage);
  };

  sendMethod('onReady');

  return {
    send: sendMethod,
    destroy
  };
};

export default CrossWindowRPC;
